import { sqliteTable, text, real, integer, index } from "drizzle-orm/sqlite-core";

// ============================================================================
// stock_entries — การเคลื่อนไหวสินค้า (Stock Entry)
// ============================================================================
export const stockEntries = sqliteTable("inventory_stock_entries", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  entryNo: text("entry_no").notNull().unique(),
  itemId: text("item_id").notNull(),
  warehouseId: text("warehouse_id").notNull(),
  entryType: text("entry_type", {
    enum: ["purchase", "sale", "transfer_in", "transfer_out", "adjustment", "return"],
  }).notNull(),
  qty: real("qty").notNull(), // positive for in, negative for out
  rate: real("rate").notNull().default(0), // unit cost
  amount: real("amount").notNull().default(0), // qty * rate
  // Epic #443 — valuation snapshot at the moment the stock movement is
  // recorded. For incoming stock (purchase/transfer_in/return), this is the
  // unit cost paid (mirrors `rate`). For outgoing stock (sale/transfer_out),
  // this is the company's current Moving Average cost as of this entry,
  // captured atomically before the ledger is mutated. COGS posting reads
  // `valuationAmount` directly so the sale + COGS amounts always agree.
  // NULL for legacy rows pre-dating Epic #443 (back-filled to `rate` /
  // `amount` in migration 0028).
  valuationRate: real("valuation_rate"),
  valuationAmount: real("valuation_amount"),
  // Issue #503 — inventory valuation GL bridge status. Stock entries are
  // append-only; accounting side effects are tracked here so retry/reconcile
  // can find missing/failed GL without mutating historical movement rows.
  valuationGlStatus: text("valuation_gl_status", {
    enum: ["pending", "posting", "posted", "failed", "non_financial", "external"],
  }).notNull().default("pending"),
  valuationGlEntryIds: text("valuation_gl_entry_ids"),
  valuationGlReversalOf: text("valuation_gl_reversal_of"),
  valuationGlError: text("valuation_gl_error"),
  valuationGlNonFinancialReason: text("valuation_gl_non_financial_reason"),
  valuationGlAttempts: integer("valuation_gl_attempts").notNull().default(0),
  valuationGlLastAttemptAt: text("valuation_gl_last_attempt_at"),
  valuationGlPostedAt: text("valuation_gl_posted_at"),
  // Epic #443 — origin of the entry beyond the coarse `entryType`. Free-text
  // (no enum CHECK in SQLite) but the in-tree producers use a small vocab:
  //   "sales_invoice"   — auto stock deduction on sales invoice submit
  //   "sales_invoice_cancelled" — reverse-leg from invoice cancel
  //   "credit_note"     — return-to-stock from credit note submit
  //   "pos"             — cash sale at the till (Epic D)
  //   "manual"          — created via the stock-entries route by hand
  //   "goods_receipt" / "delivery_order" — created by the inventory doctypes
  //   "stock_transfer" / "stock_transfer_cancelled" — same-company movement;
  //       tracked as non-financial valuation GL
  // Future modules can extend without a schema migration.
  source: text("source"),
  postingDate: text("posting_date").notNull(),
  // Reference to source document
  referenceModule: text("reference_module"),
  referenceDoctype: text("reference_doctype"),
  referenceId: text("reference_id"),
  remarks: text("remarks"),
  // Audit
  createdBy: text("created_by"),
  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at"),
  updatedBy: text("updated_by"),
  deletedBy: text("deleted_by"),
  deletedAt: text("deleted_at"),
}, (table) => ({
  se_item_wh_idx: index("se_item_wh_idx").on(table.itemId, table.warehouseId, table.postingDate),
  se_company_date_idx: index("se_company_date_idx").on(table.companyId, table.postingDate),
  // Epic #443 — fast idempotency lookup for "did the sales-invoice listener
  // already post a stock entry for this invoice?" (companyId scoped).
  se_company_ref_idx: index("se_company_ref_idx").on(
    table.companyId,
    table.referenceModule,
    table.referenceDoctype,
    table.referenceId,
  ),
  se_valuation_gl_status_idx: index("se_valuation_gl_status_idx").on(
    table.companyId,
    table.valuationGlStatus,
    table.postingDate,
  ),
  se_valuation_gl_reversal_idx: index("se_valuation_gl_reversal_idx").on(
    table.companyId,
    table.valuationGlReversalOf,
  ),
}));

// Type exports
export type StockEntry = typeof stockEntries.$inferSelect;
export type NewStockEntry = typeof stockEntries.$inferInsert;
